import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { COUNTRIES } from '@wareclouds/frontend-common';

import { CONTEXT } from '@/config/consts/commons';
import { useLeadEventsCache } from '../../accountCreation';
import { clearTrainingProgress, getAnswers } from '../services';
import { calculateScore } from '../utils';
import {
  getTrainingSections,
  getTrainingVariant,
  APPROVAL_RATE_PERCENTAGE,
} from '../utils';
import { useGetQuestionnaire } from './useGetQuestionnaire';
import { usePostTrainingCertifiedEvent } from '@/features/onboarding';

export function useTrainingMenu() {
  const { t } = useTranslation(['onboarding']);
  const navigate = useNavigate();
  const { refetchLeadEvents } = useLeadEventsCache();
  const { submitCertified, isPending: isSubmittingCertified } =
    usePostTrainingCertifiedEvent();

  const variant = getTrainingVariant();
  const sections = getTrainingSections();
  const questionnaire = useGetQuestionnaire(variant);

  const [submitResult, setSubmitResult] = useState(null);

  const completedSections = useMemo(() => {
    void submitResult;
    if (!questionnaire) return [];
    const storedAnswers = getAnswers();
    return sections
      .filter((menuSection) => {
        const qSection = questionnaire.sections.find(
          (section) => section.id === menuSection.id
        );
        if (!qSection) return false;
        return qSection.questions.every(
          (question) => storedAnswers[question.globalId] != null
        );
      })
      .map((section) => section.id);
  }, [sections, questionnaire, submitResult]);

  const allCompleted = completedSections.length === sections.length;

  const translationPath = CONTEXT === COUNTRIES.BR ? COUNTRIES.BR : variant;

  const menuItems = useMemo(
    () =>
      sections.map((section, index) => ({
        id: section.id,
        label: t(`training.sections.${translationPath}.${section.id}`),
        completed: completedSections.includes(section.id),
        interactive:
          index === 0 || completedSections.includes(sections[index - 1]?.id),
      })),
    [sections, completedSections, t, translationPath]
  );

  const handleSubmit = async () => {
    if (!questionnaire) return;
    const answers = getAnswers();
    const score = calculateScore(answers, questionnaire);
    const scorePercentage = Number((score * 100).toFixed(2));
    if (scorePercentage < APPROVAL_RATE_PERCENTAGE) {
      setSubmitResult('failed');
      return;
    }
    const sent = await submitCertified();
    if (sent) {
      setSubmitResult('passed');
    }
  };

  const handleRetry = () => {
    clearTrainingProgress();
    setSubmitResult(null);
  };

  const handlePassedModalContinue = async () => {
    clearTrainingProgress();
    await refetchLeadEvents();
    navigate('../registration');
  };

  return {
    sections,
    completedSections,
    menuItems,
    allCompleted,
    approvalRatePercentage: APPROVAL_RATE_PERCENTAGE,
    submitResult,
    handleSubmit,
    handleRetry,
    handlePassedModalContinue,
    isSubmittingCertified,
  };
}
